import axios from "axios";
import { apiAxios } from "../openapi/apiAxios";
import { AuthControllerApi } from "../openapi/client";
import { BASE_PATH } from "../openapi/client/base";
import { store } from "../store";
import { resetUser } from "../store/userSlice";
import { setUserDetail } from "../store/userDetail";
import { resetSelection as resetSelectionSale } from "../store/saleSlice";
import { resetSelection as resetSelectionSelection } from "../store/selectionSlice";
import { invalidateUserCache } from ".";
import { getTenant } from "../theme";

const { httpHeader } = getTenant();

// Clean axios instance without interceptors — used for auth-related calls
// (such as token refresh) so they don't go through the standard interceptors
// that depend on an already valid access token.
const rawAxios = axios.create({ withCredentials: true });

rawAxios.defaults.headers.common["app-channel"] = httpHeader;

const rawAuthApi = new AuthControllerApi(undefined, BASE_PATH, rawAxios);

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
let migrationPromise: Promise<string | null> | null = null;
let migrationAttempted = false;

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  if (token) {
    localStorage.setItem("isLoggedIn", "true");
  }
};

export const clearAccessToken = (): void => {
  accessToken = null;
};

export const cleanSession = () => {
  clearAccessToken();
  localStorage.setItem("isLoggedIn", "false");
  delete apiAxios.defaults.headers.common["app-user-id"];
  localStorage.removeItem("app-user-id");
  store.dispatch(resetUser());
  store.dispatch(resetSelectionSale());
  store.dispatch(resetSelectionSelection());
  store.dispatch(setUserDetail({}));
  invalidateUserCache();
};

// --- Migration helpers for old cookie-based sessions ---
// TODO: will be removed on 08/2026, after all accessTokens migrated to refresh tokens

const getOldAccessTokenCookie = (): string | null => {
  const match = document.cookie.match(/(^|;\s*)accessToken=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : null;
};

const clearOldAccessTokenCookie = (): void => {
  document.cookie = "accessToken=; max-age=0; path=/";
};

// Migrate old cookie-based session to new dual-token system via /auth/refresh2
export const migrateOldSession = async (): Promise<string | null> => {
  if (migrationAttempted) return null;
  if (migrationPromise) return migrationPromise;

  migrationPromise = (async () => {
    const oldToken = getOldAccessTokenCookie();
    if (!oldToken) return null;

    try {
      const res = await rawAuthApi.refresh2({ token: oldToken });
      const token = res.data.accessToken ?? null;
      setAccessToken(token);
      clearOldAccessTokenCookie();
      return token;
    } catch {
      clearOldAccessTokenCookie();
      return null;
    } finally {
      migrationPromise = null;
      migrationAttempted = true;
    }
  })();

  return migrationPromise;
};

// Deduplicated refresh: if a refresh is already in flight, return the same promise
export const refreshAccessToken = async (): Promise<string | null> => {
  if (localStorage.getItem("isLoggedIn") === "false") return null;
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await rawAuthApi.refresh();
      const token = res.data.accessToken ?? null;
      setAccessToken(token);
      return token;
    } catch (error: any) {
      clearAccessToken();
      // Only trigger session-expired logout when the server explicitly rejects
      // the refresh token (401). Transient network errors should not log out the user.
      if (error?.response?.status === 401) {
        cleanSession();
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
